import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { saudeService } from '../../services/saude.service';
import UploadAtestadoModal from '../Saude/UploadAtestadoModal';
import DecisaoAbonoModal from '../Saude/DecisaoAbonoModal';
import TimelineSaudeModal from '../Saude/TimelineSaudeModal';
import { useToast } from '../Toast';
import {
  DocumentPlusIcon,
  ShieldCheckIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  HeartIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import type { ColaboradorAtestadoContract } from '@sstexames/shared-types';

interface ColaboradorAtestadosTabProps {
  colaboradorId: number;
  empresaId: number;
  colaboradorNome: string;
}

export default function ColaboradorAtestadosTab({
  colaboradorId,
  empresaId,
  colaboradorNome,
}: ColaboradorAtestadosTabProps) {
  const toast = useToast();
  const [modalUploadOpen, setModalUploadOpen] = useState(false);
  const [atestadoParaAbono, setAtestadoParaAbono] =
    useState<ColaboradorAtestadoContract | null>(null);
  const [timelineOpen, setTimelineOpen] = useState(false);

  const {
    data: atestados = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['atestados-colaborador', colaboradorId],
    queryFn: () => saudeService.listarAtestados({ colaborador_id: colaboradorId }),
    enabled: !!colaboradorId,
  });

  return (
    <div className="space-y-6">
      {/* Header da Aba */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white p-4 rounded-xl border border-gray-200/80 shadow-xs">
        <div>
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <HeartIcon className="w-5 h-5 text-indigo-600" />
            Atestados Médicos & Prontuário Clínico
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Registro de afastamentos, CID-10, validação documental e linha do tempo de saúde ocupacional.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setTimelineOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-700 text-xs font-semibold border border-teal-200 transition"
          >
            <HeartIcon className="w-4 h-4 text-teal-600" />
            Prontuário Longitudinal
          </button>

          <button
            type="button"
            onClick={() => setModalUploadOpen(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-xs transition"
          >
            <DocumentPlusIcon className="w-4 h-4" />
            Novo Atestado
          </button>
        </div>
      </div>

      {/* Lista de Atestados */}
      <div className="bg-white rounded-xl border border-gray-200/80 overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
            <thead className="bg-gray-50 text-gray-600 font-semibold text-left">
              <tr>
                <th className="py-3 px-4">Início Afastamento</th>
                <th className="py-3 px-4">Dias</th>
                <th className="py-3 px-4">CID-10 & Motivo</th>
                <th className="py-3 px-4">Médico / Estabelecimento</th>
                <th className="py-3 px-4">Abono RH</th>
                <th className="py-3 px-4">Autenticidade</th>
                <th className="py-3 px-4 text-right">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    <ArrowPathIcon className="w-5 h-5 animate-spin mx-auto mb-1 text-indigo-600" />
                    Carregando histórico de atestados...
                  </td>
                </tr>
              ) : atestados.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    Nenhum atestado médico registrado para este colaborador.
                  </td>
                </tr>
              ) : (
                atestados.map((atestado: ColaboradorAtestadoContract) => (
                  <tr key={atestado.id} className="hover:bg-gray-50/70 transition">
                    <td className="py-3 px-4 font-semibold text-gray-900">
                      {atestado.data_inicio
                        ? new Date(atestado.data_inicio).toLocaleDateString('pt-BR')
                        : '-'}
                    </td>
                    <td className="py-3 px-4 font-bold text-amber-700">
                      {atestado.dias_afastamento} dia(s)
                    </td>
                    <td className="py-3 px-4">
                      {atestado.motivo_cid ? (
                        <div>
                          <span className="font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200 text-xs">
                            {atestado.motivo_cid}
                          </span>
                          {atestado.motivo_cid_descricao && (
                            <p className="text-[11px] text-gray-500 mt-0.5">
                              {atestado.motivo_cid_descricao}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic text-xs">Não informado</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-gray-900 font-medium">
                        {atestado.medico_nome || 'Médico não identificado'}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {atestado.medico_crm ? `CRM ${atestado.medico_crm}/${atestado.medico_uf || ''}` : ''}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {atestado.status_administracao === 'ABONO_ACEITO' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                          <CheckCircleIcon className="w-3.5 h-3.5" />
                          Aprovado
                        </span>
                      ) : atestado.status_administracao === 'ABONO_RECUSADO' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800">
                          <XCircleIcon className="w-3.5 h-3.5" />
                          Recusado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                          <ClockIcon className="w-3.5 h-3.5" />
                          Pendente
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {atestado.status_autenticidade === 'CONFIRMADO' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-teal-50 text-teal-700 border border-teal-200">
                          <ShieldCheckIcon className="w-3 h-3 text-teal-600" />
                          Confirmado
                        </span>
                      ) : (
                        <span className="text-gray-500 text-xs">
                          {atestado.status_autenticidade}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        type="button"
                        onClick={() => setAtestadoParaAbono(atestado)}
                        className="px-2.5 py-1 rounded bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold text-xs transition"
                      >
                        Decisão RH
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Upload */}
      <UploadAtestadoModal
        isOpen={modalUploadOpen}
        onClose={() => setModalUploadOpen(false)}
        empresaId={empresaId}
        colaboradorIdPreSelecionado={colaboradorId}
        onSuccess={() => refetch()}
        toast={toast}
      />

      {/* Modal Decisão Abono */}
      <DecisaoAbonoModal
        isOpen={!!atestadoParaAbono}
        onClose={() => setAtestadoParaAbono(null)}
        atestado={atestadoParaAbono}
        onSuccess={() => refetch()}
        toast={toast}
      />

      {/* Modal Timeline Longitudinal */}
      <TimelineSaudeModal
        isOpen={timelineOpen}
        onClose={() => setTimelineOpen(false)}
        colaboradorId={colaboradorId}
        colaboradorNome={colaboradorNome}
      />
    </div>
  );
}
